import {
  Checkbox,
  FormSection,
  PrimaryButton,
  SecondaryPageTitle,
  TextInput,
  useAlert,
} from "@wit/mpesa-ui-components";
import { AlertTypeEnum } from "@wit/mpesa-ui-components/lib/context/alert/alert.context";
import { Formik, FormikHelpers } from "formik";

import i18next, { TFunction } from "i18next";
import React, { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import styled from "styled-components";
import { object, string } from "yup";
import { ConfigContext } from "../../../../app.component";
import { RoutesEnum } from "../../../../routes/routes.constants";
import { ILanguage } from "../../../../shared/models/language.model";
import LanguagesApi from "../../../../shared/services/languages.api";
import { FormLabel, PageContainer } from "../../../../shared/shared.styled";
import BundlesApi from "../bundles.api";
import { BundleTypeProvider, IBundleType, IEditBundleRequest } from "../bundles.model";
import useEmptyLanguageWarningModal from "../../../../shared/hooks/use-empty-language-warning-modal.hook";

/**
 *
 * @param value
 * @param setLanguage
 * @param availableLanguages
 */
const validateLanguages = (
  value: any,
  availableLanguages: ILanguage[],
  setShowWarning: React.Dispatch<React.SetStateAction<boolean>>,
) => {
  if (!value) {
    return false;
  }
  for (let l = 0; l < availableLanguages.length; l++) {
    if (!value[availableLanguages[l].code]) {
      setShowWarning(true);
    }
  }
  return true;
};

/**
 * Edit bundle schema
 * @param t
 * @param languages
 */
const editBundleValidationSchema = (
  t: TFunction,
  languages: ILanguage[],
  setShowWarning: React.Dispatch<React.SetStateAction<boolean>>,
) => {
  let langValidators: { [x: string]: any } = {};

  (languages as ILanguage[])?.map((lang: ILanguage) => {
    if (lang.mandatory) {
      langValidators = {
        ...langValidators,
        [lang.code]: string()
          .trim()
          .required(i18next.t("pages.editBundle.requiredField"))
          .max(255, i18next.t("pages.editBundle.maxLenDescription")),
      };
    } else {
      langValidators = {
        ...langValidators,
        [lang.code]: string().max(255, i18next.t("pages.editBundle.maxLenDescription")),
      };
    }
  });

  return object().shape({
    name: object().shape({
      translations: object()
        .shape(langValidators)
        .test("name-lang", i18next.t("pages.addBundle.requiredField"), value =>
          validateLanguages(value, languages, setShowWarning),
        ),
    }),
    description: string()
      .required(i18next.t("pages.editBundle.requiredField"))
      .max(255, i18next.t("pages.editBundle.maxLenDescription")),
  });
};

/**
 * Edit bundle
 */
const EditBundlePage = () => {
  const [t] = useTranslation();
  const { id } = useParams<any>();
  const history = useHistory();
  const dispatch = useDispatch();
  const [showAlert, , setAlertProps] = useAlert();
  const [formValues, setFormValues] = useState<IEditBundleRequest>();
  const [bundleDetails, setBundleDetails] = React.useState<IBundleType>();
  const [availableLanguages, setAvailableLanguages] = React.useState<ILanguage[]>([]);

  const { config } = useContext(ConfigContext);

  const { warningModalOpen, showWarning, setShowWarning } = useEmptyLanguageWarningModal(availableLanguages || []);
  /**
   * Submit method wrapper to show warning if needed
   * @param values
   */
  const submitWrapper = (values: IEditBundleRequest, actions: FormikHelpers<IEditBundleRequest>) => {
    if (showWarning) {
      setShowWarning(false);
      actions.setSubmitting(false);
      warningModalOpen(editBundle, values, actions);
    } else {
      setShowWarning(false);
      editBundle(values, actions);
    }
  };

  /*
   * Sets the initial state of the form
   * */
  React.useEffect(() => {
    if (bundleDetails) {
      setFormValues({
        id: bundleDetails.id,
        name: {
          key: bundleDetails.name.key,
          translations: bundleDetails.name.translations,
        },
        provider: bundleDetails.provider,
        description: bundleDetails.description ? bundleDetails.description : "",
        upsellPromo: bundleDetails.upsellPromo ? bundleDetails.upsellPromo : false,
        buyForFriend: bundleDetails.buyForFriend ? bundleDetails.buyForFriend : false,
      });
    }
  }, [bundleDetails]);

  React.useEffect(() => {
    LanguagesApi.methods
      .getAvailableLanguages()
      .then(
        res => {
          setAvailableLanguages(res.data.availableLanguages);
        },
        () => {
          setAvailableLanguages([
            { id: "1", code: "en", name: "English", urlIcon: "ic_flag_uk.png" },
            { id: "2", code: "sw", name: "Swahili (Tanzania)", urlIcon: "ic_flag_tanzania.png" },
          ]);
        },
      )
      .finally(() => {
        getBundleDetails();
      });
  }, []);

  /**
   * Gets the bundle details data
   * */
  const getBundleDetails = () => {
    BundlesApi.methods.getBundleDetails(id).then(
      res => {
        setBundleDetails(res.data);
      },
      () => {
        setAlertProps({
          title: t("pages.bundleDetails.getBundleDetailsError"),
          type: AlertTypeEnum.ERROR,
        });
        showAlert();
      },
    );
  };

  /**
   * Form submit
   * @param values
   * @param actions
   */
  const editBundle = (values: IEditBundleRequest, actions: FormikHelpers<any>) => {
    actions.setSubmitting(true);
    BundlesApi.methods.editBundle(values).then(
      () => {
        setAlertProps({
          type: AlertTypeEnum.SUCCESS,
          title: t("pages.editBundle.editSuccessDescription"),
        });
        showAlert();
        history.push(RoutesEnum.BUNDLE_DETAILS.replace(":id", bundleDetails ? bundleDetails.id : ""));
      },
      err => {
        setAlertProps({
          type: AlertTypeEnum.ERROR,
          title:
            err && err.data && err.data.status && err.data.status.message
              ? err.data.status.message
              : t("pages.editBundle.editBundleError"),
        });
        actions.setSubmitting(false);
        showAlert();
      },
    );
  };

  /**
   * Gets the form inputs for each lang
   * */
  const getFormInputs = (
    values: IEditBundleRequest,
    handleChange: ((event: React.ChangeEvent<HTMLInputElement>) => void) | undefined,
    setFieldValue: any,
    errors: any,
  ) => {
    const rows = [];
    availableLanguages &&
      availableLanguages.map((lang: ILanguage) => {
        const key = lang.code;
        rows.push({
          label: (
            <FormLabel required={availableLanguages?.find((l: ILanguage) => l.code === key)?.mandatory}>
              {t("pages.editBundle.bundleTypeName", { language: key.toUpperCase() })}
            </FormLabel>
          ),
          displayComponent: null,
          editingComponent: (
            <TextInput
              maxLength={100}
              autoComplete={"false"}
              type={"text"}
              required={availableLanguages?.find((l: ILanguage) => l.code === key)?.mandatory}
              error={
                errors.name && errors.name.translations && errors.name.translations[key]
                  ? errors.name.translations[key]
                  : undefined
              }
              value={values.name.translations[key]}
              onChange={e => setFieldValue(`name.translations[${key}]`, e.target.value)}
              placeholder={t("pages.editBundle.bundleTypeNamePlaceholder", { language: key.toUpperCase() })}
            />
          ),
        });
      });

    rows.push(
      {
        label: <FormLabel required={true}>{t("pages.editBundle.description")}</FormLabel>,
        displayComponent: null,
        editingComponent: (
          <TextInput
            maxLength={100}
            name="description"
            value={values.description}
            onChange={handleChange}
            required={true}
            error={errors.description}
            placeholder={t("pages.editBundle.descriptionPlaceholder")}
          />
        ),
      },
      {
        label: t("pages.editBundle.provider"),
        displayComponent: null,
        editingComponent: (
          <TextInput
            autoComplete={"false"}
            type={"text"}
            required={true}
            value={
              values.provider
                ? (t(`pages.editBundle.${values.provider}`) as string)
                : (t("pages.editBundle.providerPlaceholder") as string)
            }
            disabled={true}
          />
        ),
      },
    );
    if (values.provider === BundleTypeProvider.MPESA && config!.bundles?.useUpsell) {
      rows.push({
        label: t("pages.addBundle.promotions"),
        displayComponent: null,
        editingComponent: (
          <>
            <CheckboxContainer id={"upsell-promo"}>
              <Checkbox
                name={"upsellPromo"}
                data-testid={"upsell-checkbox"}
                setFieldValue={setFieldValue}
                value={values.upsellPromo}
              />
              <Label>{t("pages.addBundle.allowUpsellPromo")}</Label>
            </CheckboxContainer>
          </>
        ),
      });
    }
    rows.push({
      label: t("pages.addBundle.buyForFriend"),
      displayComponent: null,
      editingComponent: (
        <CheckboxContainer id="buy-for-friend">
          <Checkbox
            data-testid={"buy-for-friend-checkbox"}
            name={"buyForFriend"}
            setFieldValue={setFieldValue}
            value={values.buyForFriend}
          />
          <Label>{t("pages.addBundle.allowBuyForFriend")}</Label>
        </CheckboxContainer>
      ),
    });
    return <FormSection title={t("pages.editBundle.bundleTypeDetails")} rows={rows} isEditing={true} />;
  };
  return (
    <PageContainer>
      <SecondaryPageTitle
        displayInitialsCircle={false}
        title={t("pages.editBundle.title")}
        goBackFn={() => history.push(RoutesEnum.BUNDLE_DETAILS.replace(":id", bundleDetails ? bundleDetails.id : ""))}
      />
      <EditBundleContainer>
        {formValues ? (
          <Formik
            initialValues={formValues}
            validateOnBlur={true}
            validateOnChange={false}
            onSubmit={submitWrapper}
            validationSchema={editBundleValidationSchema(t, availableLanguages, setShowWarning)}
            render={({ values, handleChange, isSubmitting, handleSubmit, setFieldValue, errors }) => (
              <form onSubmit={handleSubmit}>
                {getFormInputs(values, handleChange, setFieldValue, errors)}
                <div
                  style={{
                    width: "max-content",
                    marginLeft: "auto",
                    marginTop: 36,
                  }}
                >
                  <PrimaryButton
                    redTheme={true}
                    titleLabel={t("pages.editBundle.saveBtn")}
                    type="submit"
                    loading={isSubmitting}
                    id={"save-changes-button"}
                  />
                </div>
              </form>
            )}
          />
        ) : null}
      </EditBundleContainer>
    </PageContainer>
  );
};

export default EditBundlePage;

const EditBundleContainer = styled("div")`
  display: flex;
  flex-direction: column;
  margin-top: calc(128px + 28px - 50px);
  margin-left: calc(278px - 118px + 44px);
  margin-right: calc(278px - 118px + 44px);

  form {
    > div > div {
      border-bottom: none !important;
    }
  }
`;

const CheckboxContainer = styled("div")`
  display: flex;
`;

const Label = styled("div")`
  font-family: Vodafone Rg;
  margin-left: 8px;
  font-size: 16px;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.31;
  letter-spacing: normal;
  color: ${props => props.theme.palette.midGrey};
`;
